import { useState, useEffect, useCallback } from "react";
import { getMarkersForMapFromBackend, saveAllMarkersToBackend } from "../api";

export function useMarkers(maps, selectedMap) {
  const [markers, setMarkers] = useState([]);

  // Helper for generating localStorage key for markers per map
  function getMarkersKey(map) {
    return "lootspot_markers_" + map;
  }

  // Load markers from backend and localStorage
  useEffect(() => {
    async function loadMarkers() {
      let allMarkers = [];
      for (const map of maps) {
        let globalMarkers = [];
        try {
          globalMarkers = await getMarkersForMapFromBackend(map.key);
        } catch (error) {
          console.error(`Error loading markers for ${map.key}:`, error);
        }
        let localData = [];
        const key = getMarkersKey(map.key);
        let local = localStorage.getItem(key);
        if (local) {
          try {
            const parsed = JSON.parse(local);
            if (Array.isArray(parsed)) localData = parsed;
          } catch {}
        }
        const byId = {};
        localData.forEach((m) => {
          if (m.id) byId[m.id] = m;
        });
        // Pronađi sve contest markere u localData
        const contestMarkerIds = new Set(
          localData
            .filter((m) => m.id && m.id.endsWith("_contest1"))
            .map((m) => m.id.replace(/_contest1$/, "")),
        );
        // Merge global markers s lokalnim podacima, ali ako postoji contest marker za taj marker, koristi lokalne x/y
    const merged = globalMarkers.map((m) => {
          const local = byId[m.id];
          // Ako postoji contest marker za ovaj marker, koristi lokalne x/y
          if (
            contestMarkerIds.has(m.id) &&
            local &&
            typeof local.x === "number" &&
            typeof local.y === "number"
          ) {
            return {
              ...m,
              x: local.x,
              y: local.y,
              x_offset: local.x_offset || 0,
              team: local.team || "",
              logo: local.logo || "",
              tier: local.tier || "",
      flag: local.flag || "", // učitaj zastavu iz localStorage
      slot: local.slot || "", // učitaj slot iz localStorage
              // markerName: always from backend (m.markerName)
            };
          } else {
            return {
              ...m,
              team: local?.team || "",
              logo: local?.logo || "",
              tier: local?.tier || "",
              x_offset: local?.x_offset || 0,
      flag: local?.flag || "", // učitaj zastavu iz localStorage
      slot: local?.slot || "", // učitaj slot iz localStorage
              // markerName: always from backend (m.markerName)
            };
          }
        });
        // Add contest markers (those in localData but not in globalMarkers)
        const contestMarkers = localData.filter(
          (m) => m.id && m.id.endsWith("_contest1") && !merged.some((gm) => gm.id === m.id),
        );
        allMarkers = allMarkers.concat(merged, contestMarkers);
      }
      setMarkers(allMarkers);
    }
    if (maps.length > 0) loadMarkers();
  }, [maps]);

  // Save private data to localStorage on marker changes - debounced for performance
  useEffect(() => {
    if (!markers.length) return;
    const timeoutId = setTimeout(() => {
      const key = getMarkersKey(selectedMap);
      getMarkersForMapFromBackend(selectedMap)
        .then((globalMarkers) => {
          // Save both regular and contest markers for this map
      const privateData = markers
            .filter((m) => m.map === selectedMap)
            .map((m) => ({
              id: m.id,
              map: m.map,
              x: m.x,
              y: m.y,
              team: m.team || "",
              logo: m.logo || "",
              tier: m.tier || "",
              x_offset: m.x_offset || 0,
              size: m.size || 1,
              markerName: m.markerName || "",
        flag: m.flag || "", // spremi i zastavu
        slot: m.slot || "", // spremi i slot
            }));
          localStorage.setItem(key, JSON.stringify(privateData));
        })
        .catch(() => {
          const mapMarkers = markers
            .filter((m) => m.map === selectedMap)
            .map((m) => ({
              id: m.id,
              map: m.map,
              x: m.x,
              y: m.y,
              team: m.team || "",
              logo: m.logo || "",
              tier: m.tier || "",
              x_offset: m.x_offset || 0,
              size: m.size || 1,
              markerName: m.markerName || "",
        flag: m.flag || "", // spremi i zastavu
        slot: m.slot || "", // spremi i slot
            }));
          localStorage.setItem(key, JSON.stringify(mapMarkers));
        });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [markers, selectedMap]);

  useEffect(() => {
    localStorage.setItem("lootspot_selected_map", selectedMap);
  }, [selectedMap]);

  // Marker manipulation helpers
  const addMarker = useCallback(() => {
    if (!selectedMap) return;
    setMarkers((markers) => {
      let nextNum = 1;
      const names = new Set(markers.filter((m) => m.map === selectedMap).map((m) => m.markerName));
      while (names.has("Marker" + nextNum)) nextNum++;
      const id = Date.now() + "_" + Math.random().toString(36).slice(2, 8);
      const newMarker = {
        id,
        x: 0.5,
        y: 0.5,
        team: "",
        logo: "",
        size: 1,
        markerName: "Marker" + nextNum,
        map: selectedMap,
  tier: "",
  slot: "",
      };
      const updated = [...markers, newMarker];
      const mapMarkers = updated
        .filter((m) => m.map === selectedMap && !m.id.includes("_contest"))
        .map((m) => ({
          id: m.id,
          x: m.x,
          y: m.y,
          size: m.size,
          markerName: m.markerName,
          map: m.map,
        }));
      saveAllMarkersToBackend(selectedMap, mapMarkers);
      return updated;
    });
  }, [setMarkers, selectedMap]);

  const changeMarker = useCallback(
    (changed, showDev = false) => {
      setMarkers((markers) => {
        let markerName =
          changed.markerName && changed.markerName.trim() ? changed.markerName.trim() : undefined;
        if (!markerName) markerName = "Marker_" + changed.id?.slice(-4);
        // Remove prevMarker and saveMarkerNamesToBackend logic
        const updated = markers.map((m) =>
          m.id === changed.id ? { ...m, ...changed, markerName } : m,
        );
        // DODATNA ZAŠTITA: Ako marker ima contest marker, ne šalji ništa na backend
        const hasContest = markers.some((m) => m.id === changed.id + "_contest1");
        if (hasContest) {
          // Samo update local state, ne šalji na backend
          return updated;
        }
        // DODATNA ZAŠTITA: Ako user nije u dev modu, ne šalji ništa na backend
        if (!showDev) {
          return updated;
        }
        // Save only public data to backend za relevantnu mapu
        // Ako je contest marker, ne šalji na backend
        if (changed.id && changed.id.includes("_contest")) {
          return updated;
        }
        const mapKey = changed.map || markers.find((m) => m.id === changed.id)?.map;
        if (mapKey) {
          const mapMarkers = updated
            .filter((m) => m.map === mapKey && !m.id.includes("_contest"))
            .map((m) => ({
              id: m.id,
              x: m.x,
              y: m.y,
              size: m.size,
              markerName: m.markerName,
              map: m.map,
            }));
          import("../api").then((api) => {
            api.saveAllMarkersToBackend(mapKey, mapMarkers);
          });
        }
        return updated;
      });
    },
    [setMarkers],
  );

  const removeMarker = useCallback(
    (id, showDev = false) => {
      setMarkers((markers) => {
        const updated = markers.filter((m) => m.id !== id);
        // Pronađi mapu za marker koji se briše
        const removedMarker = markers.find((m) => m.id === id);
        const mapKey = removedMarker?.map || selectedMap;
        // Samo dev: šalji na backend
        if (showDev && mapKey) {
          const mapMarkers = updated
            .filter((m) => m.map === mapKey && !m.id.includes("_contest"))
            .map((m) => ({
              id: m.id,
              x: m.x,
              y: m.y,
              size: m.size,
              markerName: m.markerName,
              map: m.map,
            }));
          import("../api").then((api) => api.saveAllMarkersToBackend(mapKey, mapMarkers));
        }
        return updated;
      });
    },
    [setMarkers, selectedMap],
  );

  // Expose reloadMarkers function to reload from backend/localStorage
  const reloadMarkers = useCallback(() => {
    if (maps.length > 0) {
      (async function loadMarkers() {
        let allMarkers = [];
        for (const map of maps) {
          let globalMarkers = [];
          try {
            globalMarkers = await getMarkersForMapFromBackend(map.key);
          } catch (error) {
            console.error(`Error loading markers for ${map.key}:`, error);
          }
          let localData = [];
          const key = getMarkersKey(map.key);
          let local = localStorage.getItem(key);
          if (local) {
            try {
              const parsed = JSON.parse(local);
              if (Array.isArray(parsed)) localData = parsed;
            } catch {}
          }
          const byId = {};
          localData.forEach((m) => {
            if (m.id) byId[m.id] = m;
          });
          // Pronađi sve contest markere u localData
          const contestMarkerIds = new Set(
            localData
              .filter((m) => m.id && m.id.endsWith("_contest1"))
              .map((m) => m.id.replace(/_contest1$/, "")),
          );
          // Merge global markers s lokalnim podacima, ali ako postoji contest marker za taj marker, koristi lokalne x/y
          const merged = globalMarkers.map((m) => {
            const local = byId[m.id];
            // Ako postoji contest marker za ovaj marker, koristi lokalne x/y
            if (
              contestMarkerIds.has(m.id) &&
              local &&
              typeof local.x === "number" &&
              typeof local.y === "number"
            ) {
              return {
                ...m,
                x: local.x,
                y: local.y,
                x_offset: local.x_offset || 0,
                team: local.team || "",
                logo: local.logo || "",
                tier: local.tier || "",
                flag: local.flag || "", // učitaj zastavu iz localStorage
                slot: local.slot || "", // učitaj slot iz localStorage
              };
            } else {
              return {
                ...m,
                team: local?.team || "",
                logo: local?.logo || "",
                tier: local?.tier || "",
                x_offset: local?.x_offset || 0,
                flag: local?.flag || "", // učitaj zastavu iz localStorage
                slot: local?.slot || "", // učitaj slot iz localStorage
              };
            }
          });
          // Add contest markers (those in localData but not in globalMarkers)
          const contestMarkers = localData.filter(
            (m) => m.id && m.id.endsWith("_contest1") && !merged.some((gm) => gm.id === m.id),
          );
          allMarkers = allMarkers.concat(merged, contestMarkers);
        }
        setMarkers(allMarkers);
      })();
    }
  }, [maps]);

  return {
    markers,
    setMarkers,
    addMarker,
    changeMarker,
    removeMarker,
    reloadMarkers,
  };
}
